import './OrderView.scss';
import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import { useCallback, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdCallMade, MdCallReceived } from 'react-icons/md';
import { formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Appearance, Placement, Size } from '@Components/designConstants';
import { Button } from '@Components/Button';
import { BPMTable, TypedHeader } from '@Components/StickyTable/BPMTable';
import { useSetError } from '../../../redux/modals';
import { makeMemoizedGetter } from '../../../utils/data';
import { download } from '../../../utils/download-utils';
import { S3SignedUrlFetch, assertPresignedOk } from '../../../utils/fetch-utils';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { getLocalChangesForCompany } from '../../../utils/streaming-buying-storage';
import {
  OrderPdfEntry,
  OrderProblem,
  OrderProblemType,
  statusToLabel,
  StreamingOrder
} from './order-view-utils';

const DATE_FORMAT = 'M/d/yyyy';
const PDF_MIME_TYPE = 'application/pdf';
const FILTER_KEYS = [OrderProblem.UNSAVED, OrderProblem.UNSENT, OrderProblem.UNCONFIRMED] as const;

const toPrettyDate = makeMemoizedGetter({
  calculate: R.pipe(Dfns.parseISO, Dfns.format(DATE_FORMAT))
});

const parseAndFormatSpend = makeMemoizedGetter({
  calculate: (num: string | number | null) => formatMoney(parseFloat(`${num ?? 0}`))
});

type OrderRow = StreamingOrder & { problem: '' | OrderProblemType };

const renderNoOrders = () => <div className='no-orders'>No orders to show.</div>;

interface OrderViewProps {
  orderInfo: StreamingOrder[];
  pdfInfo?: Record<string, OrderPdfEntry>;
  forceCommit: (id: string | number) => Promise<void>;
}

const ListCell = ({
  children,
  placement = Placement.TOP_START
}: {
  children: string;
  placement?: typeof Placement.TOP_START | typeof Placement.TOP_END;
}) => (
  <OverlayTrigger placement={placement} overlay={<Tooltip>{children}</Tooltip>}>
    <div className='list-cell'>{children}</div>
  </OverlayTrigger>
);

export const OrderView = ({ orderInfo, pdfInfo, forceCommit }: OrderViewProps) => {
  const setError = useSetError();
  const { cid: company } = useCompanyInfo();

  const [orders, unsavedCount, unsentCount, unconfirmedCount] = useMemo(() => {
    const unsavedMap = getLocalChangesForCompany(company);
    let unsent = 0;
    let unconfirmed = 0;
    const computed: OrderRow[] = orderInfo.map(order => {
      let problem: OrderRow['problem'] = '';
      if (unsavedMap[order.id]) {
        problem = OrderProblem.UNSAVED;
      } else if (order.status === 0) {
        problem = OrderProblem.UNSENT;
        unsent++;
      } else if (order.status < 20) {
        problem = OrderProblem.UNCONFIRMED;
        unconfirmed++;
      }
      return { ...order, problem };
    });
    return [computed, Object.keys(unsavedMap).length, unsent, unconfirmed] as const;
  }, [orderInfo, company]);

  const downloadPDF = useCallback(
    async (filename: string) => {
      try {
        const response = await S3SignedUrlFetch(`bpm-streaming-orders/${filename}`);
        assertPresignedOk(response, 'PDF download failed');
        const blob: Blob = await response.blob();
        download(blob, filename, PDF_MIME_TYPE);
      } catch (e) {
        setError({ message: 'Could not find the file.', reportError: e });
      }
    },
    [setError]
  );

  const [buttonFilter, setButtonFilter] = useState<OrderProblemType | ''>('');
  const filteredOrders = useMemo(
    () => (buttonFilter ? orders.filter(order => order.problem === buttonFilter) : orders),
    [orders, buttonFilter]
  );

  const headers: TypedHeader<OrderRow>[] = useMemo(
    () => [
      {
        label: 'ID',
        name: 'id',
        width: 100,
        renderer: data => (
          <div>
            <a href={`/${company}/streaming/buying/${data.network}/${data.id}`}>
              {data.id}.{data.revision}
            </a>
          </div>
        )
      },
      {
        label: 'Problem',
        name: 'problem',
        width: 120
      },
      {
        label: 'Network',
        name: 'network',
        width: 100,
        renderer: data => <ListCell>{data.network}</ListCell>
      },
      {
        label: 'Platforms',
        name: 'platforms',
        flex: 1,
        minFlexWidth: 120,
        renderer: data => <ListCell>{(data.platforms ?? []).join(', ')}</ListCell>
      },
      {
        label: 'Descriptions',
        name: 'descriptions',
        flex: 2,
        minFlexWidth: 150,
        renderer: data => (
          <ListCell>
            {(data.descriptions ?? []).map(description => description || '(none)').join(', ')}
          </ListCell>
        )
      },
      {
        label: 'Start',
        name: 'start',
        width: 110,
        renderer: data => toPrettyDate(data.start)
      },
      {
        label: 'End',
        name: 'end',
        width: 110,
        renderer: data => toPrettyDate(data.end)
      },
      {
        label: 'Spend',
        name: 'spend',
        width: 120,
        renderer: data => parseAndFormatSpend(data.spend)
      },
      {
        label: 'Status',
        name: 'status',
        width: 140,
        renderer: data => {
          const label = statusToLabel(data.status);
          if (data.status >= 20) return label;
          return (
            <Button
              appearance={Appearance.EMPTY}
              size={Size.SM}
              onClick={() => forceCommit(data.id)}
            >
              {label}
            </Button>
          );
        }
      },
      {
        label: 'Last User',
        name: 'lastuser',
        width: 80,
        renderer: data => (
          <ListCell placement={Placement.TOP_END}>{(data.lastuser ?? '').split('@')[0]}</ListCell>
        )
      },
      {
        label: 'PDFs',
        name: '',
        width: 80,
        nonInteractive: true,
        renderer: data => {
          if (!pdfInfo) return <div />;
          const pdfs = pdfInfo[data.id];
          const sent = pdfs?.sent;
          const approved = pdfs?.approved;
          return (
            <div className='pdf-buttons'>
              {sent && (
                <Button
                  appearance={Appearance.OUTLINED}
                  size={Size.SM}
                  onClick={() => downloadPDF(sent)}
                  leadingElement={<MdCallMade />}
                />
              )}
              {approved && (
                <Button
                  appearance={Appearance.OUTLINED}
                  size={Size.SM}
                  onClick={() => downloadPDF(approved)}
                  leadingElement={<MdCallReceived />}
                />
              )}
            </div>
          );
        }
      }
    ],
    [company, downloadPDF, forceCommit, pdfInfo]
  );

  const filterCounts: Record<OrderProblemType, number> = {
    [OrderProblem.UNSAVED]: unsavedCount,
    [OrderProblem.UNSENT]: unsentCount,
    [OrderProblem.UNCONFIRMED]: unconfirmedCount
  };

  return (
    <div className='order-view' data-testid='order-view'>
      <div className='filter-buttons'>
        {FILTER_KEYS.map(key => {
          const isSelected = key === buttonFilter;
          const count = filterCounts[key];
          return (
            <Button
              key={key}
              appearance={isSelected ? Appearance.FILLED : Appearance.OUTLINED}
              size={Size.SM}
              onClick={() => setButtonFilter(isSelected ? '' : key)}
            >
              {key} {count ? `(${count})` : ''}
            </Button>
          );
        })}
      </div>
      <div className='order-table' data-testid='order-table'>
        <BPMTable
          noRowsRenderer={renderNoOrders}
          data={filteredOrders}
          rowHeight={30}
          headers={headers}
        />
      </div>
    </div>
  );
};
